import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot } from "lexical";
import { useDeferredValue, useEffect, useRef, useState } from "react";

function getCharCount() {
  const root = $getRoot();
  // Collect all leaf-level block nodes (paragraphs, list items, headings, etc.)
  // so that nested structures like lists are counted per-item, not per-container.
  const blocks = [];
  const walk = (node) => {
    const children = node.getChildren ? node.getChildren() : [];
    if (children.length === 0 || children.every((c) => !c.getChildren)) {
      // Leaf block — no block-level children
      blocks.push(node);
    } else {
      children.forEach(walk);
    }
  };
  walk(root);

  let count = 0;
  for (let i = 0; i < blocks.length; i++) {
    if (i > 0) count += 1; // one newline between blocks
    count += blocks[i].getTextContentSize();
  }
  return count;
}

/**
 * CharacterLimitPlugin — displays a character counter below the editor and
 * optionally notifies the parent when the limit is exceeded.
 *
 * @param {number}   charLimit        — The character limit to display and check against.
 * @param {function} [onLimitReached] — Optional callback fired with `true` when count exceeds the limit, `false` otherwise.
 */
export default function CharacterLimitPlugin({ charLimit, onLimitReached }) {
  const [editor] = useLexicalComposerContext();
  const [charCount, setCharCount] = useState(0);
  const deferredCount = useDeferredValue(charCount);
  const prevCountRef = useRef(0);
  const onLimitReachedRef = useRef(onLimitReached);
  onLimitReachedRef.current = onLimitReached;

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const count = getCharCount();
        if (count !== prevCountRef.current) {
          prevCountRef.current = count;
          setCharCount(count);
        }
      });
    });
  }, [editor]);

  // Fire the callback based on the deferred count to avoid blocking input
  useEffect(() => {
    if (typeof onLimitReachedRef.current === "function") {
      onLimitReachedRef.current(deferredCount > charLimit);
    }
  }, [deferredCount, charLimit]);

  const exceeded = charCount > charLimit;

  return (
    <div className="editor-char-count" aria-live="polite">
      <span style={{ color: exceeded ? "#E62429" : undefined }}>
        {charCount}/{charLimit}
      </span>
    </div>
  );
}
